"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { cn } from "@repo/ui/utils";
import { Share2, Check, Download, Loader2 } from "lucide-react";
import type { ArticleActionsProps } from "./article-actions.types";
export function ArticleActions({
  className,
  article
}: ArticleActionsProps) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard API unavailable
    }
  }, []);
  const handleDownload = useCallback(async () => {
    if (!article || downloading) return;
    setDownloading(true);
    try {
      const {
        generateArticlePdf
      } = await import("@/lib/article-pdf/generate-article-pdf");
      await generateArticlePdf(article);
    } catch (err) {
      console.error("[article-pdf] download failed", err);
    } finally {
      setDownloading(false);
    }
  }, [article, downloading]);
  return <div className={cn("flex flex-col gap-4", className)} data-testid="article-actions">
      <button type="button" onClick={handleShare} className="flex w-fit cursor-pointer items-center gap-2 py-1 text-text-primary transition-colors hover:text-text-secondary focus-visible:outline-none">
        {copied ? <Check className="size-5" strokeWidth={2} /> : <Share2 className="size-5" strokeWidth={2} />}
        <span className="typography-body-sm-medium leading-tight">
          {copied ? "Copied" : "Share"}
        </span>
      </button>

      <button type="button" onClick={handleDownload} disabled={!article || downloading} aria-busy={downloading} className="flex w-fit cursor-pointer items-center gap-2 py-1 text-text-primary transition-colors hover:text-text-secondary focus-visible:outline-none disabled:cursor-default disabled:opacity-50">
        {downloading ? <Loader2 className="size-5 animate-spin" strokeWidth={2} /> : <Download className="size-5" strokeWidth={2} />}
        <span className="typography-body-sm-medium leading-tight">
          {downloading ? "Preparing…" : "Download"}
        </span>
      </button>
    </div>;
}